import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useNavigate} from 'react-router-dom'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {Button} from '../../../../_gori/partials/widgets'
import {isEmpty} from 'lodash'
import {USPS_MANIFEST_SERVICES} from '../../../../_gori/constants'
import {ROUTES} from '../../../../_gori/constants/_routes'
import {SET_UP_MODAL_ALL} from '../core/_const'
import ShipmentService from '../../shipments/core/_requests'
import {ActionAllDoneModal} from './ActionAllDoneModal'
import {INSURANCE_TYPE, OrderType} from '../../orders/core/_const'
import {InsufficientBalanceModal} from '../../orders/modals/InsufficientBalanceModal'

type ActiveModal = 'progress' | 'done' | 'insufficient_balance'

type Props = {
  show: boolean
  setUpModal: {
    key: string
    title: string
    process: string
    failed: string
    countCallApi: number
    notice: {
      title: string
      success: string
      failed: string
      showPrintLabels: boolean
    }
  }
  isShipments?: boolean
  isSelectAll?: boolean
  data: any
  handleClose: () => void
  dataAutoSetting?: any
  extraPayload?: Record<string, any>
}

const ActionAllModal: React.FC<Props> = ({
  setUpModal,
  show,
  data,
  handleClose,
  isSelectAll = false,
  isShipments = false,
  dataAutoSetting,
  extraPayload = {},
}) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {newCancelToken, isCancel} = useCancelToken()
  const [processRow, setProcessRow] = useState<number>(0)
  const [failedRow, setFailedRow] = useState<number>(0)
  const [percentage, setPercentage] = useState<number>(0)
  const [activeModal, setActiveModal] = useState<ActiveModal>('progress')
  const [noticeArrNotice, setNoticeArrNotice] = useState<Array<object>>([])
  const [insufficientBalanceLogId, setInsufficientBalanceLogId] = useState<string | number>()

  const arrCallApi = useMemo(() => {
    const arrNew: any = []

    for (let i = 0; i < data.length; i += setUpModal.countCallApi) {
      arrNew.push(data.slice(i, i + setUpModal.countCallApi))
    }
    return arrNew
  }, [data, setUpModal.countCallApi])

  // Use ref to track if autoCallApi has been called to prevent infinite loop
  const hasCalledApi = React.useRef(false)
  const insufficientBalanceHit = React.useRef(false)

  const autoCallApi = useCallback(async () => {
    const results: any = []
    let close = false

    for (let i = 0; i < arrCallApi.length; i++) {
      if (close || insufficientBalanceHit.current) break
      try {
        if (setUpModal.key === SET_UP_MODAL_ALL.CREATE_LABELS.key) {
          await Promise.all(
            arrCallApi[i].map(async (row) => {
              const _result: any = {}
              try {
                // Determine per-order payload: only include immediate_manifest for qualifying USPS orders
                let orderPayload = {...extraPayload}
                if (extraPayload?.immediate_manifest) {
                  const carrier = row.original?.carrier
                  const service = row.original?.service
                  const fullService = carrier && service ? `${carrier}_${service}` : null
                  if (!fullService || !USPS_MANIFEST_SERVICES.includes(fullService)) {
                    orderPayload = {...extraPayload, immediate_manifest: false}
                  }
                }

                const payload = {
                  id: row.original.id,
                  ...orderPayload,
                  ...(dataAutoSetting !== undefined &&
                    dataAutoSetting?.status === true && {
                      insurance_option: {
                        provider: INSURANCE_TYPE.BAE_GUARD,
                        insured_amount: dataAutoSetting.value,
                      },
                    }),
                }

                const response: any =
                  row.original?.store?.store_type === OrderType.TEMU
                    ? await ShipmentService.storeTemu(payload, {cancelToken: newCancelToken()})
                    : await ShipmentService.store(payload, {cancelToken: newCancelToken()})
                _result.success = response
                if (!response.label_url) {
                  setFailedRow((prev) => prev + 1)
                } else {
                  setProcessRow((prev) => prev + 1)
                }
                return response
              } catch (err: any) {
                if (err?.response?.data?.error?.name === 'METER:INSUFFICIENT_BALANCE') {
                  insufficientBalanceHit.current = true
                  setInsufficientBalanceLogId(err?.response?.data?.error?.log_id)
                  setActiveModal('insufficient_balance')
                  return err
                }
                _result.error = err.response
                setFailedRow((prev) => prev + 1)
                return err
              } finally {
                if (!isEmpty(_result)) {
                  _result.data = row.original
                  results.push(_result)
                }
              }
            })
          )
        }
        if (setUpModal.key === SET_UP_MODAL_ALL.REFUND_REQUEST.key) {
          await Promise.all(
            arrCallApi[i].map((row) => {
              const _result: any = {}
              return ShipmentService.refund(row.original.id, {}, {cancelToken: newCancelToken()})
                .then((response) => {
                  _result.success = response
                  if (!response.status) {
                    setFailedRow((prev) => prev + 1)
                  } else {
                    setProcessRow((prev) => prev + 1)
                  }
                })
                .catch((err) => {
                  _result.error = err.response
                  setFailedRow((prev) => prev + 1)
                })
                .finally(() => {
                  if (!isEmpty(_result)) {
                    _result.data = row.original
                    results.push(_result)
                  }
                })
            })
          )
        }
        if (setUpModal.key === SET_UP_MODAL_ALL.RETURN_LABELS.key) {
          await Promise.all(
            arrCallApi[i].map((row) => {
              const _result: any = {}
              return ShipmentService.printReturnLabel(row.data_payload, {
                cancelToken: newCancelToken(),
              })
                .then((response) => {
                  _result.success = response
                  if (!response.label_url) {
                    setFailedRow((prev) => prev + 1)
                  } else {
                    setProcessRow((prev) => prev + 1)
                  }
                })
                .catch((err) => {
                  _result.error = err.response
                  setFailedRow((prev) => prev + 1)
                })
                .finally(() => {
                  if (!isEmpty(_result)) {
                    _result.data = row
                    results.push(_result)
                  }
                })
            })
          )
        }
        if (setUpModal.key === SET_UP_MODAL_ALL.PACKING_SLIPS.key) {
          if (isSelectAll) {
            await Promise.all(
              // eslint-disable-next-line no-loop-func
              arrCallApi[i].map((row) => {
                const _result: any = {}
                const data = row.original
                return ShipmentService.storePackingSlip(
                  {order_id: data?.id},
                  {cancelToken: newCancelToken()}
                )
                  .then((res) => {
                    _result.success = res
                    setProcessRow((prev) => prev + 1)
                  })
                  .catch((err: any) => {
                    if (isCancel(err)) {
                      close = true
                    }
                    _result.error = err.response
                    setFailedRow((prev) => prev + 1)
                  })
                  .finally(() => {
                    if (!isEmpty(_result)) {
                      _result.data = row.original
                      results.push(_result)
                    }
                  })
              })
            )
          } else {
            await Promise.all(
              // eslint-disable-next-line no-loop-func
              arrCallApi[i].map((row) => {
                const _result: any = {}
                let payload: any
                if (isShipments) {
                  const dataShipments = row.original
                  payload = {
                    gori_shipment_id: dataShipments.id,
                    order_id: dataShipments.order_id ?? null,
                  }
                } else {
                  const dataOrder = row.original
                  payload = {
                    order_id: dataOrder.id,
                  }
                }

                return ShipmentService.storePackingSlip(payload, {cancelToken: newCancelToken()})
                  .then((res) => {
                    _result.success = res
                    setProcessRow((prev) => prev + 1)
                  })
                  .catch((err: any) => {
                    if (isCancel(err)) {
                      close = true
                    }
                    _result.error = err.response
                    setFailedRow((prev) => prev + 1)
                  })
                  .finally(() => {
                    if (!isEmpty(_result)) {
                      _result.data = row.original
                      results.push(_result)
                    }
                  })
              })
            )
          }
        }
      } catch (error) {
        if (isCancel(error)) return
      }
    }
    if (!insufficientBalanceHit.current) {
      setNoticeArrNotice(results)
      setActiveModal('done')
    }
  }, [
    setUpModal.key,
    isShipments,
    arrCallApi,
    newCancelToken,
    isSelectAll,
    isCancel,
    dataAutoSetting,
    extraPayload,
  ])

  const handleAddFunds = useCallback(async () => {
    handleClose()
    navigate(ROUTES.DEPOSITS.BASE)
  }, [navigate, handleClose])

  useEffect(() => {
    setPercentage(Math.round(((processRow + failedRow) / data.length) * 100))
  }, [processRow, failedRow, data.length])

  useEffect(() => {
    // Only call autoCallApi once when modal opens
    if (!hasCalledApi.current) {
      hasCalledApi.current = true
      autoCallApi()
    }
  }, [autoCallApi])

  return (
    <>
      {activeModal === 'insufficient_balance' && (
        <InsufficientBalanceModal
          show
          handleClose={() => {
            setActiveModal('progress')
            handleClose()
          }}
          onAddFunds={handleAddFunds}
          logId={insufficientBalanceLogId}
          saveBeforeRedirect={false}
        />
      )}
      {activeModal === 'done' && (
        <ActionAllDoneModal
          setUpModal={setUpModal.notice}
          show
          handleClose={() => {
            setActiveModal('progress')
            handleClose()
          }}
          arrNotice={noticeArrNotice}
        />
      )}
      {activeModal === 'progress' && (
        <Modal
          id='gori_action_all_modal'
          tabIndex={-1}
          aria-hidden='true'
          centered
          dialogClassName='modal-size-md h-auto'
          contentClassName='modal-padding'
          show={show}
          onHide={handleClose}
          backdrop='static'
        >
          <Modal.Header>
            <Modal.Title bsPrefix={'fw-bolder fs-1'}>
              {intl.formatMessage({id: setUpModal.title})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              {intl.formatMessage(
                {id: setUpModal.process},
                {process: processRow, total: data.length}
              )}
            </div>
            <div className='text-danger'>
              {failedRow > 0 && intl.formatMessage({id: setUpModal.failed}, {failed: failedRow})}
            </div>
            <div className='mt-5'>
              <div className='progress h-30px'>
                <div
                  role='progressbar'
                  className='progress-bar bg-image-gori'
                  style={{width: `${percentage}%`}}
                  aria-valuenow={percentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  {percentage >= 100 ? 100 : Math.ceil(percentage)}%
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className={`btn btn-${percentage >= 100 ? 'success' : 'danger'} d-flex`}
              label={
                percentage >= 100
                  ? intl.formatMessage({id: 'DONE'})
                  : intl.formatMessage({id: 'STOP'})
              }
              event={handleClose}
            />
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

export {ActionAllModal}
